// #1241 Mail-Render Helpers — extractCleanContent strips WeShip-Layout-Boilerplate
// (Logo, Claim-Banner, Footer-Signatur) und Outlook/Gmail-Quote-Blocks aus
// Mail-Bodies, damit der eigentliche Inhalt sichtbar wird.

const FOOTER_PATTERNS = [
  /WeShip\s+Fulfillment\s+GmbH/i,
  /\+43\s*316\s*375\s*093/,
  /help@weship\.eu/i,
  /www\.weship\.eu/i,
  /Kundenservice/i,
];

function isLayoutOutbound(html) {
  if (!html) return false;
  return /weship-email-layout/i.test(html)
    || /<img[^>]+alt=["']WeShip["']/i.test(html)
    || /<img[^>]+src=["'][^"']*email-logo/i.test(html);
}

function stripLogoImages(root) {
  root.querySelectorAll('img').forEach(img => {
    const alt = (img.getAttribute('alt') || '').toLowerCase();
    const src = (img.getAttribute('src') || '').toLowerCase();
    if (alt === 'weship' || src.includes('email-logo') || src.includes('weship-logo')) {
      img.remove();
    }
  });
}

function stripClaimBanner(root) {
  root.querySelectorAll('table, td, tr, div').forEach(el => {
    const text = (el.textContent || '').trim();
    if (!text) return;
    if (text.length < 40 && /^Claim\s+CLM-\d+/i.test(text)) {
      const closestTable = el.closest('table');
      (closestTable || el).remove();
    }
  });
}

function stripFooterTables(root) {
  root.querySelectorAll('table').forEach(t => {
    const text = (t.textContent || '').trim();
    if (text.length > 350) return;
    let hits = 0;
    for (const pat of FOOTER_PATTERNS) if (pat.test(text)) hits++;
    if (hits >= 2) t.remove();
  });
}

function stripQuoteBlocks(root) {
  const selectors = [
    '#mail-editor-reference-message-container',
    '.ms-outlook-mobile-reference-message',
    '.gmail_quote',
    'blockquote.gmail_quote',
    'blockquote[type="cite"]',
    'div.OutlookMessageHeader',
    'div[id*="reference-message"]',
  ];
  root.querySelectorAll(selectors.join(',')).forEach(el => el.remove());
}

function pickContentCell(root) {
  const candidates = Array.from(root.querySelectorAll('td')).filter(td => {
    const style = (td.getAttribute('style') || '').replace(/\s+/g, '');
    return /padding:40px/i.test(style) || /padding:30px/i.test(style);
  });
  if (candidates.length === 0) return null;
  candidates.sort((a, b) => (b.textContent?.length || 0) - (a.textContent?.length || 0));
  return candidates[0];
}

function stripInlineSignature(html) {
  if (!html) return '';
  return html
    .replace(/(<br\s*\/?>\s*){0,6}\s*(?:—\s*<br\s*\/?>\s*)?We\s*Ship\s+Fulfillment\s+GmbH[\s\S]*$/i, '')
    .replace(/(<br\s*\/?>\s*){0,6}\s*(?:Carrier|Customer)\s*(?:&amp;|&)?\s*Support\s+Manager[\s\S]*$/i, '')
    .replace(/(<br\s*\/?>\s*){0,4}\s*--\s*<br[\s\S]*$/i, '')
    .replace(/(<br\s*\/?>\s*)+$/i, '')
    .trim();
}

function stripEmptyWrappers(root) {
  let changed = true;
  let safety = 5;
  while (changed && safety-- > 0) {
    changed = false;
    root.querySelectorAll('td, div, span').forEach(el => {
      if (el.children.length === 0 && !(el.textContent || '').trim()) {
        el.remove();
        changed = true;
      }
    });
  }
}

export function extractCleanContent(html) {
  if (!html || typeof html !== 'string') return '';
  if (typeof window === 'undefined' || typeof DOMParser === 'undefined') return html;
  try {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const body = doc.body;
    if (!body) return html;

    const isOutbound = isLayoutOutbound(html);

    stripLogoImages(body);
    stripClaimBanner(body);
    stripFooterTables(body);
    stripQuoteBlocks(body);

    let inner;
    if (isOutbound) {
      const cell = pickContentCell(body);
      inner = cell ? cell.innerHTML : body.innerHTML;
      inner = stripInlineSignature(inner);
    } else {
      const wrap = doc.createElement('div');
      wrap.innerHTML = body.innerHTML;
      stripEmptyWrappers(wrap);
      inner = wrap.innerHTML;
    }

    return inner.trim() || html;
  } catch (e) {
    return html;
  }
}

export function plaintextToHtml(text) {
  if (!text) return '';
  const escaped = String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const paragraphs = escaped
    .split(/\n{2,}/)
    .map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`)
    .join('');
  return paragraphs || `<p>${escaped}</p>`;
}

export function renderMailBody(msg) {
  if (!msg) return '';
  if (msg.body_html) return extractCleanContent(msg.body_html);
  if (msg.body_text) return plaintextToHtml(msg.body_text);
  if (msg.body) {
    const hasTags = /<[a-z][\s\S]*>/i.test(msg.body);
    return hasTags ? extractCleanContent(msg.body) : plaintextToHtml(msg.body);
  }
  return '';
}

export function hasOriginalHtml(msg) {
  if (!msg) return false;
  if (msg.body_html) return true;
  if (msg.body && /<[a-z][\s\S]*>/i.test(msg.body)) return true;
  return false;
}

export function getOriginalHtml(msg) {
  if (!msg) return '';
  return msg.body_html || msg.body_full || msg.body || '';
}
